import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useDeliveries, useDeliveryReadinessBoard } from '@/hooks/useDelivery';
import type { DeliveryReadinessRow, DeliveryReadinessBoardFilters } from '@/services/delivery.service';
import { DeliveryReadinessKpiBar } from '@/components/sales/DeliveryReadinessKpiBar';
import { DeliveryReadinessCard } from '@/components/sales/DeliveryReadinessCard';
import { CreateDeliveryFromReadinessModal } from '@/components/sales/CreateDeliveryFromReadinessModal';
import type { DeliveryNote } from '@/services/delivery.service';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const { Title, Text } = Typography;
const { Option } = Select;

const EXECUTION_SCOPES = [
  'Fabrication',
  'Manufacturing',
  'Repair',
  'Repair + Manufacturing',
  'Outsource',
];

const DeliveryReadinessDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [customerQ, setCustomerQ] = useState('');
  const [soQ, setSoQ] = useState('');
  const [woQ, setWoQ] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [executionScope, setExecutionScope] = useState<string | undefined>();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ready');

  const debouncedCustomer = useDebouncedValue(customerQ.trim(), 300);
  const debouncedSo = useDebouncedValue(soQ.trim(), 300);
  const debouncedWo = useDebouncedValue(woQ.trim(), 300);
  const debouncedSearch = useDebouncedValue(searchQ.trim(), 300);

  const filters = useMemo<DeliveryReadinessBoardFilters>(
    () => ({
      customer_id: debouncedCustomer || undefined,
      sales_order_id: debouncedSo || undefined,
      work_order_id: debouncedWo || undefined,
      search: debouncedSearch || undefined,
      execution_scope: executionScope,
    }),
    [debouncedCustomer, debouncedSo, debouncedWo, debouncedSearch, executionScope]
  );

  const historySearch = useMemo(() => {
    // Prefer free-text; otherwise pass SO / customer typed values so history respects the same filters.
    return debouncedSearch || debouncedSo || debouncedCustomer || undefined;
  }, [debouncedSearch, debouncedSo, debouncedCustomer]);

  const { data: board, isLoading, refetch } = useDeliveryReadinessBoard(filters);
  const { data: historyList = [], isLoading: historyLoading } = useDeliveries({
    date_from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    search: historySearch,
  });

  const kpis = board?.kpis ?? {
    readyToday: 0,
    readyUnits: 0,
    blockedCount: 0,
    pendingDispatches: 0,
    todaysDeliveryNotes: 0,
    overdueDeliveries: 0,
  };

  const readyRows = board?.ready ?? [];
  const partialRows = board?.partial ?? [];
  const blockedRows = board?.blocked ?? [];

  const selectedRows = useMemo(
    () => readyRows.filter((r) => selectedKeys.includes(r.row_key)),
    [readyRows, selectedKeys]
  );

  const selectionSoId = selectedRows[0]?.sales_order_id ?? null;
  const selectionCustomerId = selectedRows[0]?.customer_id ?? null;
  const canCreate =
    selectedRows.length > 0 &&
    selectedRows.every(
      (r) =>
        r.sales_order_id === selectionSoId &&
        r.customer_id === selectionCustomerId &&
        r.readiness_status === 'ready'
    );

  const handleSelect = (row: DeliveryReadinessRow, checked: boolean) => {
    setSelectedKeys((prev) => {
      if (!checked) return prev.filter((k) => k !== row.row_key);
      if (prev.length === 0) return [row.row_key];
      const first = readyRows.find((r) => r.row_key === prev[0]);
      if (
        first &&
        (first.sales_order_id !== row.sales_order_id || first.customer_id !== row.customer_id)
      ) {
        return [row.row_key];
      }
      return [...prev, row.row_key];
    });
  };

  const renderCardGrid = (rows: DeliveryReadinessRow[], selectable: boolean) => {
    if (rows.length === 0) {
      return <Empty description="No items in this view" />;
    }
    return (
      <Row gutter={[12, 12]}>
        {rows.map((row) => (
          <Col key={row.row_key} xs={24} sm={12} lg={8} xl={6}>
            <DeliveryReadinessCard
              row={row}
              selectable={selectable}
              selected={selectedKeys.includes(row.row_key)}
              onSelectChange={handleSelect}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const tableColumns: ColumnsType<DeliveryReadinessRow> = [
    { title: 'Customer', dataIndex: 'customer_name', ellipsis: true },
    { title: 'SO', dataIndex: 'sales_order_number', width: 120 },
    { title: 'WO', dataIndex: 'work_order_number', width: 120 },
    {
      title: 'Item code',
      dataIndex: 'item_code',
      width: 120,
      render: (v: string | null) => v || '—',
    },
    { title: 'Item', dataIndex: 'item_description', ellipsis: true },
    {
      title: 'Unit',
      dataIndex: 'production_unit_number',
      width: 70,
      render: (v: number | null) => (v != null ? v : '—'),
    },
    { title: 'Available', dataIndex: 'available_quantity', width: 90 },
    {
      title: 'Status',
      dataIndex: 'readiness_status',
      width: 90,
      render: (s: string) => s,
    },
    {
      title: 'Reasons',
      dataIndex: 'reasons',
      ellipsis: true,
      render: (r: string[]) => r.join('; '),
    },
  ];

  const historyColumns: ColumnsType<DeliveryNote> = [
    {
      title: 'DN',
      dataIndex: 'delivery_note_number',
      render: (val: string, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/delivery-notes/${row.id}`)}>
          {val}
        </Button>
      ),
    },
    {
      title: 'SO',
      dataIndex: ['salesOrder', 'sales_order_number'],
      render: (val: string, row) =>
        val ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/orders/${row.sales_order_id}`)}>
            {val}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Date',
      dataIndex: 'delivery_date',
      render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'delivery_status',
      render: (s: string) => s,
    },
  ];

  const tabItems = [
    {
      key: 'ready',
      label: `Ready (${readyRows.length})`,
      children: (
        <>
          <Space style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canCreate}
              onClick={() => setCreateOpen(true)}
            >
              Create delivery note ({selectedRows.length})
            </Button>
            {!canCreate && selectedRows.length > 0 && (
              <Text type="secondary">Select rows from the same customer and customer order only.</Text>
            )}
          </Space>
          {renderCardGrid(readyRows, true)}
          <Card size="small" title="Detail grid" style={{ marginTop: 16 }}>
            <Table
              size="small"
              rowKey="row_key"
              dataSource={readyRows}
              columns={tableColumns}
              pagination={{ pageSize: 10 }}
              loading={isLoading}
            />
          </Card>
        </>
      ),
    },
    {
      key: 'partial',
      label: `Partially ready (${partialRows.length})`,
      children: renderCardGrid(partialRows, false),
    },
    {
      key: 'blocked',
      label: `Blocked (${blockedRows.length})`,
      children: (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Blocked items cannot be added to a delivery note until the listed issues are resolved."
          />
          {renderCardGrid(blockedRows, false)}
        </>
      ),
    },
    {
      key: 'history',
      label: 'Delivered history',
      children: (
        <Table
          size="small"
          rowKey="id"
          loading={historyLoading}
          dataSource={historyList}
          columns={historyColumns}
          pagination={{ pageSize: 15 }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Delivery Readiness
          </Title>
          <Text type="secondary">Dispatch control board — ready units, blocked shipments, and delivery history</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Space>

      <DeliveryReadinessKpiBar kpis={kpis} loading={isLoading} />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input
              allowClear
              placeholder="Customer name or code"
              value={customerQ}
              onChange={(e) => setCustomerQ(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input
              allowClear
              placeholder="Customer order #"
              value={soQ}
              onChange={(e) => setSoQ(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input
              allowClear
              placeholder="Work order #"
              value={woQ}
              onChange={(e) => setWoQ(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              allowClear
              placeholder="Execution scope"
              style={{ width: '100%' }}
              value={executionScope}
              onChange={(v) => setExecutionScope(v)}
            >
              {EXECUTION_SCOPES.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search item code / item / project…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
          </Col>
        </Row>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      <CreateDeliveryFromReadinessModal
        open={createOpen}
        rows={selectedRows}
        onClose={() => {
          setCreateOpen(false);
          setSelectedKeys([]);
        }}
        onSuccess={(id) => {
          setSelectedKeys([]);
          navigate(`/growth/delivery-notes/${id}`);
        }}
      />
    </div>
  );
};

export default DeliveryReadinessDashboardPage;
